<template>
  <div>
    <v-breadcrumbs :items="items" class="pb-0"></v-breadcrumbs>

    <div class="px-4 pt-2">
      <h2 class="text-h5 font-weight-bold mb-1">OT Bill List</h2>
      <div class="caption grey--text mb-4">{{ reportInfoLabel }}</div>
    </div>

    <!-- Filters -->
    <v-card class="mx-4 mb-4" outlined>
      <v-card-title class="subtitle-1 py-3">Search Filters</v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete
              v-model="search_doctor_id"
              :items="doctors"
              item-text="dr_name"
              item-value="id"
              label="Doctor"
              prepend-icon="mdi-doctor"
              clearable
              dense
              outlined
              hide-details
              :loading="loadingstatus"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu v-model="startDateMenu" :close-on-content-click="false" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="start_date"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </template>
              <v-date-picker v-model="start_date" :max="end_date" @input="onStartDateSelect"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu v-model="endDateMenu" :close-on-content-click="false" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="end_date"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </template>
              <v-date-picker v-model="end_date" :min="start_date" @input="onEndDateSelect"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              label="Search (OT ID, Patient, Operation)"
              dense
              outlined
              hide-details
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="2" class="d-flex align-center">
            <v-btn color="primary" class="mr-2" @click="printList">
              <v-icon left small>mdi-printer</v-icon>
              Print
            </v-btn>
            <v-btn color="warning" outlined @click="otBillList">
              <v-icon left small>mdi-book-clock</v-icon>
              Schedule
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Summary Row -->
    <v-card class="mx-4 mb-4" outlined>
      <v-card-text class="py-2 px-0">
        <v-simple-table dense class="summary-table">
          <tbody>
            <tr>
              <td
                v-for="card in summaryCards"
                :key="card.label"
                class="text-center summary-cell"
              >
                <div class="caption grey--text text-no-wrap">{{ card.label }}</div>
                <div class="subtitle-2 font-weight-bold text-no-wrap" :class="card.color">{{ card.value }}</div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <!-- Operation Type Filter -->
    <v-card class="mx-4 mb-4" outlined>
      <v-card-text class="py-3">
        <div class="subtitle-2 mb-2">Filter by Operation Type</div>
        <div class="statusClass flex-wrap">
          <v-btn
            v-for="(button, index) in buttonTypes"
            :key="index"
            small
            class="text-none mr-2 mb-1"
            :color="isSelected(button.operationType) ? operationButtonColor(button.operationType) : 'default'"
            :outlined="!isSelected(button.operationType)"
            @click="selectButton(button.operationType)"
          >
            <v-icon left small>{{ button.icon }}</v-icon>
            {{ button.title }} ({{ getTotalOperationCount(button.operationType) }})
          </v-btn>
        </div>
        <v-divider class="my-3"></v-divider>
        <div class="d-flex flex-wrap align-center">
          <span class="subtitle-2 mr-3">Status:</span>
          <v-chip
            v-for="(status, index) in statusSummary"
            :key="index"
            :color="getChipColor(status.status)"
            :text-color="getTextColor(status.status)"
            class="ma-1"
            small
            outlined
          >
            {{ status.status.toUpperCase() }}: {{ status.count }}
          </v-chip>
          <v-chip v-if="!statusSummary.length" class="ma-1" small outlined>No data</v-chip>
        </div>
      </v-card-text>
    </v-card>

    <!-- Data Table -->
    <v-card class="mx-4 mb-4">
      <v-card-title class="subtitle-1 py-3">
        OT Bill Records
        <v-spacer></v-spacer>
        <span class="caption">Showing {{ desserts.length }} record(s)</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-data-table
        :headers="filteredHeaders"
        :items="itemsWithSno"
        class="elevation-0"
        dense
        :loading="loadingstatus"
        loading-text="Loading... Please wait"
      >
        <template v-slot:item.opp_date="{ item }">
          {{ formatDate(item.opp_date) }}
        </template>
        <template v-slot:item.operation_subhead="{ item }">
          <span v-if="item.operation">{{ item.operation.name }}</span>
          <v-chip v-if="item.operation_subhead" class="ma-1" color="error" outlined x-small>
            {{ item.operation_subhead }}
          </v-chip>
        </template>
        <template v-slot:item.opp_status="{ item }">
          <v-chip :color="getColor(item.opp_status)" dark label outlined x-small>
            {{ item.opp_status.toUpperCase() }}
          </v-chip>
        </template>
        <template v-slot:item.paid_amount="{ item }">
          <div class="text-right font-weight-medium">{{ b_number_format(item.paid_amount) }}</div>
        </template>
        <template v-slot:item.hospital_amount="{ item }">
          <div class="text-right font-weight-medium">{{ b_number_format(item.hospital_amount) }}</div>
        </template>
        <template v-slot:item.doctor_amount="{ item }">
          <div class="text-right font-weight-medium">{{ b_number_format(item.doctor_amount) }}</div>
        </template>
        <template v-slot:item.ext_surgeon_amount="{ item }">
          <div class="text-right font-weight-medium">{{ b_number_format(item.ext_surgeon_amount) }}</div>
        </template>
        <template v-slot:body.append v-if="desserts.length">
          <tr class="font-weight-bold grey lighten-4">
            <td :colspan="summaryColspan">Grand Total</td>
            <td class="text-right">{{ b_number_format(amountSummary.paid) }}</td>
            <td v-if="showAmountColumns" class="text-right">{{ b_number_format(amountSummary.hospital) }}</td>
            <td v-if="showAmountColumns" class="text-right">{{ b_number_format(amountSummary.doctor) }}</td>
            <td class="text-right">{{ b_number_format(amountSummary.ext_doctor) }}</td>
            <td></td>
            <td></td>
          </tr>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top v-if="checkPermissionHasOrNot(OT_SCHEDULE_REGISTRATION_DETAILS)">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                  @click="ot_details(item)"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="ma-2"
                  color="orange"
                  label
                  outlined
                  small
              >
                <v-icon> mdi-chevron-double-right</v-icon>
              </v-chip>
            </template>
            <span>OT Registration Details</span>
          </v-tooltip>
          <v-tooltip top v-if="checkPermissionHasOrNot(OT_BILL_REFUND)">
            <template v-if="item.opp_status == 'open'" v-slot:activator="{ on, attrs }">
              <v-chip
                  @click="ot_refund(item)"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="ma-2"
                  open
                  color="primary"
                  label
                  outlined
                  small
              >
                <v-icon> mdi-undo-variant</v-icon>
              </v-chip>
            </template>
            <span>Refund to Patient</span>
          </v-tooltip>
          <v-tooltip top v-if="item.paid_amount && item.operation.op_type === 'Operation' ">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                  @click="admissionform(item)"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="ma-2"
                  open
                  color="primary"
                  label
                  outlined
                  small
              >
                <v-icon left> mdi-message-text</v-icon>
                <v-icon left> mdi-chevron-double-right</v-icon>
              </v-chip>
            </template>
            <span>Admission form</span>
          </v-tooltip>
          <v-tooltip top v-if="item.paid_amount && item.operation.op_type === 'Injection'">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                  @click="injection(item)"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="ma-2"
                  open
                  color="primary"
                  label
                  outlined
                  small
              >
                <v-icon left> mdi-message-text</v-icon>
                <v-icon left> mdi-chevron-double-right</v-icon>
              </v-chip>
            </template>
            <span>Injection Form</span>
          </v-tooltip>
          <v-tooltip top v-if="item.paid_amount && item.operation.op_type === 'Laser'">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                  @click="yaglaser(item)"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="ma-2"
                  open
                  color="primary"
                  label
                  outlined
                  small
              >
                <v-icon left> mdi-message-text</v-icon>
                <v-icon left> mdi-chevron-double-right</v-icon>
              </v-chip>
            </template>
            <span>Laser Consent Form</span>
          </v-tooltip>
          <v-tooltip top v-if="item.paid_amount && item.operation.op_type === 'Laser'">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                  @click="laserChart(item)"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="ma-2"
                  open
                  color="primary"
                  label
                  outlined
                  small
              >
                <v-icon left> mdi-message-text</v-icon>
                <v-icon left> mdi-chevron-double-right</v-icon>
              </v-chip>
            </template>
            <span>Laser Chart</span>
          </v-tooltip>
          <v-tooltip top v-if="item.paid_amount && item.operation.consent_paper_type === 'Cataract' ">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                  @click="ConsentForCataractSurgery(item)"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  class="ma-2"
                  open
                  color="primary"
                  label
                  outlined
                  small
              >
                <v-icon left> mdi-message-text</v-icon>
                <v-icon left> mdi-chevron-double-right</v-icon>
              </v-chip>
            </template>
            <span>Cataract Surgery</span>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="text-center pa-4 grey--text">No OT bill records found for selected criteria.</div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>
<style scoped>
.statusClass {
  display: flex;
  flex-wrap: wrap;
}
.summary-table {
  width: 100%;
}
.summary-table table {
  table-layout: fixed;
  width: 100%;
}
.summary-cell {
  border-right: 1px solid #e0e0e0;
  padding: 8px 6px !important;
  white-space: nowrap;
}
.summary-cell:last-child {
  border-right: none;
}
</style>
<script>
import atshq from "../../../../js/setup";
import moment from "moment/moment";
import PermissionName from "@/js/PermissionName";

export default {
  data() {
    return {
      search: "",
      start_date: moment().format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
      startDateMenu: false,
      endDateMenu: false,
      loadingstatus: true,
      search_doctor_id: '',
      user_id: localStorage.getItem("userId"),
      doctors: [],
      headers: [
        {text: "Serial #", value: "sno",},
        {text: "OT ID", value: "registration_no",},
        {text: "ID", value: "patient_id",},
        {text: "Patient Name", value: "patient.patient_name"},
        {text: "Doctor", value: "doctor.dr_name"},
        {text: "Operation Name", value: "operation_subhead"},
        {text: "OT Date", value: "opp_date", Key: "Date"},
        {text: "Paid Amount", value: "paid_amount"},
        {text: "Hospital Amount", value: "hospital_amount"},
        {text: "Doctor Amount", value: "doctor_amount"},
        {text: "Ext. Doctor Amount", value: "ext_surgeon_amount"},
        {text: "Status", value: "opp_status"},
        {text: "Action", value: "actions", sortable: false},
      ],

      buttonTypes: [
        {title: 'Total Operation', operationType: 'totalOperation', icon: 'mdi mdi-restore'},
        {title: 'Operation', operationType: 'Operation', icon: 'mdi-camera-gopro'},
        {title: 'Injection', operationType: 'Injection', icon: 'mdi-needle'},
        {title: 'Laser', operationType: 'Laser', icon: 'mdi-television-ambient-light'},
        {title: 'Cancel', operationType: 'cancel', icon: 'mdi-cancel'}
      ],
      totalOperationTypeWiseCount: [
        {op_type: 'Injection', total_operation: 0,},
        {op_type: 'Laser', total_operation: 0,},
        {op_type: 'Operation', total_operation: 0,},
        {op_type: 'totalOperation', total_operation: 0,},
        {op_type: 'cancel', total_operation: 0,}
      ],
      selectedOperationType: 'totalOperation',
      totalOperation: 0,
      total_cancel: 0,
      total_operation_count: 0,
      statusSummary: [],
      desserts: [],
      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "Operations",
          disabled: true,
          href: "patients",
        },
      ],
      returnApiBaseUrl: atshq.defaults.headers.common.api_base_URL,
      OT_SCHEDULE_REGISTRATION_DETAILS: PermissionName.OT_SCHEDULE_REGISTRATION_DETAILS,
      OT_BILL_REFUND: PermissionName.OT_BILL_REFUND,
    };
  },
  created() {
    this.initialize();
    this.getDoctors();
    this.operationButtonColor(this.selectedOperationType);
  },
  watch: {
    search_doctor_id() {
      this.initialize();
    },
    search() {
      this.initialize();
    },
  },
  computed: {
    dateQuery() {
      const range = this.normalizedDateRange;
      return range ? range.join(",") : "";
    },
    normalizedDateRange() {
      if (!this.start_date || !this.end_date) {
        return null;
      }

      const range = [this.start_date, this.end_date].sort();
      return range;
    },
    isDateRangeComplete() {
      return this.normalizedDateRange !== null;
    },
    itemsWithSno() {
      return this.desserts.map((d, index) => ({...d, sno: index + 1}));
    },
    filteredHeaders() {
      if (this.selectedOperationType === 'Operation' || this.selectedOperationType === 'Injection' || this.selectedOperationType === 'Laser') {
        return this.headers.filter(function (header) {
          return header.value !== 'hospital_amount' && header.value !== 'doctor_amount' && header.value !== 'sno';
        });
      }
      return this.headers;
    },
    showAmountColumns() {
      return this.selectedOperationType === 'totalOperation' || this.selectedOperationType === 'cancel';
    },
    summaryColspan() {
      const amountCols = this.showAmountColumns ? 4 : 2;
      return this.filteredHeaders.length - amountCols - 2;
    },
    amountSummary() {
      return this.desserts.reduce(function (totals, item) {
        totals.paid += parseFloat(item.paid_amount || 0);
        totals.ot_amount += parseFloat(item.ot_amount || 0);
        totals.hospital += parseFloat(item.hospital_amount || 0);
        totals.doctor += parseFloat(item.doctor_amount || 0);
        totals.ext_doctor += parseFloat(item.ext_surgeon_amount || 0);
        return totals;
      }, { paid: 0, ot_amount: 0, hospital: 0, doctor: 0, ext_doctor: 0 });
    },
    selectedDoctorName() {
      if (!this.search_doctor_id) {
        return 'All Doctors';
      }
      const doctor = this.doctors.find(function (d) {
        return d.id === this.search_doctor_id;
      }.bind(this));
      return doctor ? doctor.dr_name : 'Selected Doctor';
    },
    reportInfoLabel() {
      const typeLabel = this.selectedOperationType === 'totalOperation'
        ? 'All Operations'
        : this.selectedOperationType;
      return this.formatDate(this.start_date) + ' to ' + this.formatDate(this.end_date)
        + ' | ' + typeLabel + ' | ' + this.selectedDoctorName;
    },
    summaryCards() {
      const openCount = this.desserts.filter(function (item) {
        return item.opp_status === 'open';
      }).length;
      const closeCount = this.desserts.filter(function (item) {
        return item.opp_status === 'close';
      }).length;
      const cancelCount = this.desserts.filter(function (item) {
        return item.opp_status === 'cancel';
      }).length;
      return [
        { label: 'Total Records', value: this.desserts.length, color: '' },
        { label: 'Open', value: openCount, color: 'orange--text' },
        { label: 'Closed', value: closeCount, color: 'green--text' },
        { label: 'Cancelled', value: cancelCount, color: 'red--text' },
        { label: 'Total Paid', value: this.b_number_format(this.amountSummary.paid), color: 'primary--text' },
        { label: 'OT Amount', value: this.b_number_format(this.amountSummary.ot_amount), color: 'primary--text' },
        { label: 'Hospital', value: this.b_number_format(this.amountSummary.hospital), color: '' },
        { label: 'Doctor', value: this.b_number_format(this.amountSummary.doctor), color: '' },
        { label: 'Ext. Doctor', value: this.b_number_format(this.amountSummary.ext_doctor), color: '' },
      ];
    },
  },
  methods: {
    getDoctors() {
      atshq
          .get("doctors?status=1")
          .then((response) => {
            this.doctors = response.data.data;
          })
          .catch((e) => {
            this.errors.push(e);
          });
    },

    getTotalOperationCount(operationType) {
      switch (operationType) {
        case 'Injection':
        case 'Laser':
        case 'Operation': {
          const operation = this.totalOperationTypeWiseCount.find(function (op) {
            return op.op_type === operationType;
          });
          return operation && operation.total_operation ? operation.total_operation : 0;
        }
        case 'totalOperation': {
          return (this.total_operation_count || 0) + (this.total_cancel || 0);
        }
        case 'cancel': {
          return this.total_cancel || 0;
        }
        default: {
          return 0;
        }
      }
    },
    validateSelectedDate() {
      if (!this.start_date || !this.end_date) {
        this.$noty.error("Please select start and end date");
        return false;
      }

      if (this.start_date > this.end_date) {
        this.end_date = this.start_date;
      }

      return true;
    },
    onStartDateSelect() {
      this.startDateMenu = false;
      if (this.end_date && this.start_date > this.end_date) {
        this.end_date = this.start_date;
      }
      this.applyDateFilter();
    },
    onEndDateSelect() {
      this.endDateMenu = false;
      if (this.start_date && this.end_date < this.start_date) {
        this.start_date = this.end_date;
      }
      this.applyDateFilter();
    },
    applyDateFilter() {
      if (!this.isDateRangeComplete) {
        return;
      }
      if (!this.validateSelectedDate()) {
        return;
      }
      this.initialize();
    },
    initialize() {
      if (!this.isDateRangeComplete) {
        this.loadingstatus = false;
        return;
      }
      if (!this.validateSelectedDate()) {
        this.loadingstatus = false;
        return;
      }

      this.loadingstatus = true;
      atshq
          .get("ot-bill-list?date=" + this.dateQuery + "&operation_type=" + this.selectedOperationType + "&doctor_id=" + this.search_doctor_id + "&search_query=" + this.search)
          .then((response) => {
            this.desserts = response.data.data;
            this.totalOperation = response.data.totalOperation;
            this.statusSummary = response.data.statusSummary;
            this.totalOperationTypeWiseCount = response.data.totalOperationTypeWiseCount;
            this.total_cancel = response.data.total_cancel;
            this.total_operation_count = response.data.total_operation_count;
            this.loadingstatus = false;
          })
          .catch((e) => {
            this.loadingstatus = false;
            const message = e.response && e.response.data && e.response.data.message
              ? e.response.data.message
              : "Failed to load OT bill list";
            this.$noty.error(message);
          });
    },
    selectButton(operationType) {
      this.operationButtonColor(operationType);
      this.selectedOperationType = operationType;
      this.initialize();
    },
    isSelected(operationType) {
      return this.selectedOperationType === operationType;
    },
    b_number_format(n) {
      const amount = parseFloat(n || 0);
      return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatDate(value) {
      if (!value) {
        return '-';
      }
      return moment(value).format('DD MMM, YYYY');
    },
    getStatusCount(status) {
      const row = this.statusSummary.find(function (s) {
        return s.status === status;
      });
      return row ? row.count : 0;
    },
    getColor(status) {
      if (status == "open") return "orange";
      else if (status == "close") return "green";
      else if (status == "cancel") return "red";
    },
    printList() {
      if (!this.isDateRangeComplete) {
        this.$noty.error("Please select start and end date");
        return;
      }
      if (!this.validateSelectedDate()) {
        return;
      }
      window.open(
        this.returnApiBaseUrl
          + "print/ot-bill-print?date="
          + this.dateQuery
          + "&user_id="
          + this.user_id
          + "&doctor_id="
          + this.search_doctor_id
          + "&operation_type="
          + this.selectedOperationType
      );
    },
    ot_details(item) {
      window.location.href = "/ot-registration/" + item.registration_no;
    },
    ot_refund(item) {
      window.location.href = "/otbill-refund/" + item.registration_no;
    },
    otBillList() {
      window.location.href = "/ot-module/schedule";
    },

    admissionform(item) {
      window.open(
          this.returnApiBaseUrl + "print/op-consent-paper/" + item.registration_no + "?user_id=" + this.user_id + "&operation_type=admissionForm"
      );
    },
    injection(item) {
      window.open(
          this.returnApiBaseUrl + "print/op-consent-paper/" + item.registration_no + "?user_id=" + this.user_id + "&operation_type=injection"
      );
    },
    yaglaser(item) {
      window.open(
          this.returnApiBaseUrl + "print/op-consent-paper/" + item.registration_no + "?user_id=" + this.user_id + "&operation_type=laser"
      );
    },
    laserChart(item) {
      window.open(
          this.returnApiBaseUrl + "print/op-consent-paper/" + item.registration_no + "?user_id=" + this.user_id + "&operation_type=chart"
      );
    },
    ConsentForCataractSurgery(item) {
      window.open(
          this.returnApiBaseUrl + "print/consent-for-cataract/" + item.registration_no + "?user_id=" + this.user_id
      );
    },
    getChipColor(status) {
      switch (status) {
        case 'close':
          return 'success';
        case 'open':
          return 'warning';
        case 'cancel':
          return 'red';
        default:
          return 'blue';
      }
    },
    getTextColor(status) {
      switch (status) {
        case 'close':
          return 'success';
        case 'open':
          return 'warning';
        case 'cancel':
          return 'red';
        default:
          return 'blue';
      }
    },
    operationButtonColor(btnStatus) {
      switch (btnStatus) {
        case 'cancel':
          return 'red';
        default:
          return 'success';
      }
    },

  },
};
</script>
